import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PasswordInput } from "@/components/PasswordInput";
import ImageUpload from "@/components/ImageUpload";
import { Loader2, Plus, Pencil, KeyRound, Users, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", role: "SUPERVISOR", unit_ids: [], all_units: false, ativo: true, password: "", send_invite: false, avatar_path: null };

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [units, setUnits] = useState([]);
  const [modal, setModal] = useState(false);
  const [pwModal, setPwModal] = useState(null); // user id
  const [newPw, setNewPw] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const roleOptions = Object.keys(ROLE_LABELS).filter((r) => me.role === "SUPER_ADMIN" ? true : r !== "SUPER_ADMIN");

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); api.get("/units").then((r) => setUnits(r.data)); }, [load]);

  const open = (u) => {
    if (u) { setForm({ ...EMPTY, ...u, password: "" }); setEditId(u.id); }
    else { setForm(EMPTY); setEditId(null); }
    setModal(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleUnit = (id) => setForm((f) => ({ ...f, unit_ids: f.unit_ids.includes(id) ? f.unit_ids.filter((x) => x !== id) : [...f.unit_ids, id] }));

  const isAdminRole = ["SUPER_ADMIN", "MASTER"].includes(form.role);

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Nome e e-mail obrigatórios");
    if (editId) {
      const target = users?.find((u) => u.id === editId);
      const emailChanged = form.email.trim().toLowerCase() !== (target?.email || "").toLowerCase();
      if (emailChanged && !window.confirm("Alterar o e-mail muda o login de acesso deste usuário. Deseja continuar?")) return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/users/${editId}`, {
          name: form.name, email: form.email, phone: form.phone, role: form.role,
          unit_ids: form.unit_ids, all_units: form.all_units, ativo: form.ativo,
          avatar_path: form.avatar_path || null,
        });
      } else {
        const { data } = await api.post("/users", {
          name: form.name, email: form.email, phone: form.phone, role: form.role,
          unit_ids: form.unit_ids, all_units: form.all_units, ativo: form.ativo,
          password: form.send_invite ? undefined : (form.password || undefined),
          send_invite: form.send_invite, avatar_path: form.avatar_path || null,
        });
        if (form.send_invite) {
          toast.success(data.convite_enviado ? "Usuário criado e convite enviado por e-mail" : "Usuário criado (falha ao enviar convite — use Reenviar)");
        }
      }
      if (!editId && form.send_invite) { setModal(false); load(); return; }
      toast.success(editId ? "Usuário atualizado" : "Usuário criado");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const resetPw = async () => {
    if (newPw.length < 6) return toast.error("Mínimo 6 caracteres");
    try { await api.post(`/users/${pwModal}/reset-password`, { password: newPw }); toast.success("Senha redefinida"); setPwModal(null); setNewPw(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleAtivo = async (u) => {
    try { await api.put(`/users/${u.id}`, { ativo: !u.ativo }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Excluir o usuário "${u.name}"? Só é possível se não houver histórico. Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Usuário excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const resendInvite = async (u) => {
    try { await api.post(`/users/${u.id}/invite`); toast.success("Convite reenviado por e-mail"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao reenviar convite"); }
  };

  const unitLabel = (u) => u.all_units ? "Todas as unidades" : (u.unit_ids?.length ? `${u.unit_ids.length} unidade(s)` : "—");

  const activeMasters = (users || []).filter((u) => u.role === "MASTER" && u.ativo).length;
  const isLastMaster = (u) => u.role === "MASTER" && u.ativo && activeMasters <= 1;

  if (!["SUPER_ADMIN", "MASTER"].includes(me.role)) {
    return <Card className="p-10 text-center text-muted-foreground" data-testid="usuarios-page">Você não tem permissão para acessar o módulo de Usuários.</Card>;
  }

  return (
    <div className="space-y-5" data-testid="usuarios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Configurações</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Users className="w-8 h-8 text-primary" /> Usuários</h1>
        </div>
        <Button onClick={() => open(null)} data-testid="btn-novo-usuario"><Plus className="w-4 h-4" /> Novo Usuário</Button>
      </div>

      {!users ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid gap-3">
          {users.map((u) => (
            <Card key={u.id} className="bg-card/70 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`usuario-${u.email}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{u.name}</span>
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-primary/15 text-primary border border-primary/40 font-mono">{ROLE_LABELS[u.role] || u.role}</span>
                  {!u.ativo && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 font-mono">Inativo</span>}
                </div>
                <div className="text-xs text-muted-foreground">{u.email} · {u.phone || "sem telefone"} · {unitLabel(u)}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-xs" title={isLastMaster(u) ? "Último Master ativo — não pode ser desativado" : ""}>
                  <span className="text-muted-foreground">Ativo</span>
                  <Switch checked={u.ativo} disabled={isLastMaster(u)} onCheckedChange={() => toggleAtivo(u)} data-testid={`toggle-${u.email}`} />
                </div>
                <Button variant="outline" size="sm" onClick={() => setPwModal(u.id)} data-testid={`reset-${u.email}`}><KeyRound className="w-4 h-4" /></Button>
                {u.role !== "SUPER_ADMIN" && (
                  <Button variant="outline" size="sm" onClick={() => resendInvite(u)} title="Reenviar convite por e-mail" data-testid={`invite-${u.email}`}><Mail className="w-4 h-4" /></Button>
                )}
                <Button variant="outline" size="sm" onClick={() => open(u)} data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4" /></Button>
                {u.role !== "SUPER_ADMIN" && u.id !== me.id && (
                  <Button variant="outline" size="sm" className="text-red-400" disabled={isLastMaster(u)}
                    title={isLastMaster(u) ? "Último Master ativo — não pode ser excluído" : ""}
                    onClick={() => delUser(u)} data-testid={`del-user-${u.email}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar" : "Novo"} Usuário</DialogTitle>
            <DialogDescription>Defina perfil e unidades autorizadas. O escopo é validado no backend.</DialogDescription></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nome completo</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="u-name" /></div>
            <div className="sm:col-span-2"><ImageUpload label="Foto de perfil (avatar)" value={form.avatar_path} onChange={(p) => set("avatar_path", p)} testid="usuario-avatar" /></div>
            <div><Label>E-mail / Login</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="u-email" /></div>
            <div><Label>Telefone (opcional)</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="u-phone" /></div>
            <div>
              <Label>Perfil</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="u-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!editId && !form.send_invite && <div><Label>Senha inicial</Label><PasswordInput value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="padrão: nexolog123" data-testid="u-password" /></div>}
          </div>

          {!editId && (
            <label className="flex items-center gap-2 text-sm cursor-pointer bg-secondary/40 rounded-md px-3 py-2">
              <Switch checked={form.send_invite} onCheckedChange={(v) => set("send_invite", v)} data-testid="u-send-invite" />
              <span>Enviar convite por e-mail (o usuário cria a própria senha)</span>
            </label>
          )}

          {isAdminRole ? (
            <div className="text-sm text-muted-foreground bg-secondary/40 rounded-md p-3">Este perfil possui acesso a <b>todas as unidades</b> automaticamente.</div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Unidades autorizadas</Label>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={form.all_units} onCheckedChange={(v) => set("all_units", v)} data-testid="u-all-units" /> Todas as unidades</label>
              </div>
              {!form.all_units && (
                <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto border border-border rounded-md p-2">
                  {units.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-secondary/40">
                      <input type="checkbox" checked={form.unit_ids.includes(u.id)} onChange={() => toggleUnit(u.id)} data-testid={`u-unit-${u.codigo}`} />
                      <span>{u.codigo} — {u.nome} {u.tipo === "CD" && <span className="text-primary text-[10px]">(CD)</span>}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-usuario">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal reset senha */}
      <Dialog open={!!pwModal} onOpenChange={(o) => !o && setPwModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display uppercase">Redefinir Senha</DialogTitle>
            <DialogDescription>Defina uma nova senha (mínimo 6 caracteres) para este usuário.</DialogDescription></DialogHeader>
          <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nova senha (mín. 6)" data-testid="reset-pw-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwModal(null)}>Cancelar</Button>
            <Button onClick={resetPw} data-testid="btn-confirmar-reset">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
